import type { ReactNode } from 'react';

import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { Box, Typography } from '@/shared/ui';

import { spacing } from '@/config/ui';
import type { Sx } from '@/shared/types/uiTypes';
import { Dialog } from '@/shared/ui/overlays/Modal';
import Button from '@/shared/ui/primitives/Button';

type SaveErrorVariant = 'new' | 'rename' | 'save';

type SaveErrorDialogProps = {
  open: boolean;
  isNew: boolean;
  isRename: boolean;
  onCancel: () => void;
  onDiscard: () => void;
  onRetry: () => void;
};

const dialogSx: Sx = { maxWidth: 560, width: '100%' };
const contentSx: Sx = { minWidth: 0 };

const headerSx: Sx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  mb: spacing[1.5]
};

const buttonsSx: Sx = { display: 'flex', gap: 2, width: '100%' };
const buttonSx: Sx = {
  flex: 1,
  height: 33,
  '&.Mui-focusVisible': { outline: 'none', boxShadow: 'none' }
};

const getVariant = (isNew: boolean, isRename: boolean): SaveErrorVariant => {
  if (isNew) return 'new';
  if (isRename) return 'rename';
  return 'save';
};

const getTitle = (variant: SaveErrorVariant): string => {
  switch (variant) {
    case 'new':
      return "Dashboard couldn't be created";
    case 'rename':
      return "Rename couldn't be saved";
    case 'save':
      return "Changes couldn't be saved";
  }
};

const getDescription = (variant: SaveErrorVariant): ReactNode => {
  switch (variant) {
    case 'new':
      return (
        <>
          <Typography>Something went wrong.</Typography>{' '}
          <Typography>Try again or cancel.</Typography>
        </>
      );
    case 'rename':
      return (
        <>
          <Typography>Something went wrong.</Typography>
          <Typography>The dashboard has been restored to its previous name and URL.</Typography>
        </>
      );
    case 'save':
      return (
        <>
          <Typography>Something went wrong. Your draft is still here.</Typography>
          <Typography>Retry or discard to restore to last saved version.</Typography>
        </>
      );
  }
};

export const SaveErrorDialog = ({
  open,
  isNew,
  isRename,
  onCancel,
  onDiscard,
  onRetry
}: SaveErrorDialogProps) => {
  const variant = getVariant(isNew, isRename);

  return (
    <Dialog
      buttons={
        <Box sx={buttonsSx}>
          <Button
            size='small'
            sx={buttonSx}
            variant='outlined'
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            size='small'
            sx={buttonSx}
            variant='outlined'
            onClick={onRetry}
          >
            {variant === 'new' ? 'Try again' : 'Retry'}
          </Button>
          {variant !== 'new' && (
            <Button
              size='small'
              sx={buttonSx}
              variant='outlined'
              onClick={onDiscard}
            >
              Discard changes
            </Button>
          )}
        </Box>
      }
      disableBackdropClick
      actionsSx={{ padding: 0 }}
      contentSx={contentSx}
      open={open}
      sx={dialogSx}
      onClose={onCancel}
    >
      <Box sx={headerSx}>
        <PriorityHighIcon sx={{ fontSize: 24 }} />
        <Typography variant='subtitle2'>{getTitle(variant)}</Typography>
      </Box>
      {getDescription(variant)}
    </Dialog>
  );
};
